import type { ServerMessage } from '@shared/protocol';

interface ForcedMinigameWsClient {
  send(message: unknown): void;
  subscribe(handler: (message: unknown) => void): () => void;
}

interface QueueGodModeForcedMinigameOptions {
  readonly wsClient: ForcedMinigameWsClient;
  readonly minigameId: string;
  readonly waitForGodModeEnabled: boolean;
  readonly timeoutMs?: number;
  readonly setTimeoutFn?: (handler: () => void, timeoutMs: number) => unknown;
  readonly clearTimeoutFn?: (timeoutId: unknown) => void;
  readonly log?: (event: {
    readonly minigameId: string;
    readonly sourceType: ServerMessage['type'];
    readonly roomKeys: readonly string[];
  }) => void;
}

function isBootForceGodModeEnabledMessage(msg: ServerMessage): boolean {
  return msg.type === 'notification' && msg.text === 'god_mode_enabled';
}

function isBootForceGodModeInvalidMessage(msg: ServerMessage): boolean {
  return msg.type === 'notification' && msg.text === 'god_mode_invalid';
}

function bootForceRoomKeys(msg: ServerMessage): string[] {
  switch (msg.type) {
    case 'room_created':
      return [`room:${msg.roomId}`, `code:${msg.code}`];
    case 'quick_play_joined': {
      const keys = [`room:${msg.roomId}`];
      if (typeof msg.roomCode === 'string' && msg.roomCode !== '')
        keys.push(`code:${msg.roomCode}`);
      return keys;
    }
    case 'room_joined':
      if (msg.mode === 'warmup-arena') return [];
      return [`room:${msg.roomId}`, `code:${msg.roomCode}`];
    case 'session_restored':
      if (msg.mode === 'warmup-arena') return [];
      return [`code:${msg.roomCode}`];
    default:
      return [];
  }
}

export function queueGodModeForcedMinigame(options: QueueGodModeForcedMinigameOptions): () => void {
  const {
    wsClient,
    minigameId,
    waitForGodModeEnabled,
    timeoutMs = 120_000,
    setTimeoutFn = (handler, ms) => globalThis.setTimeout(handler, ms),
    clearTimeoutFn = (timeoutId) =>
      globalThis.clearTimeout(timeoutId as ReturnType<typeof setTimeout>),
    log,
  } = options;
  let godModeReady = !waitForGodModeEnabled;
  let timeoutId: unknown;
  let pendingRoomMessage: ServerMessage | null = null;
  const sentRoomKeys = new Set<string>();

  const maybeSendForRoom = (msg: ServerMessage): void => {
    const roomKeys = bootForceRoomKeys(msg);
    if (roomKeys.length === 0) return;
    if (!godModeReady) {
      pendingRoomMessage = msg;
      return;
    }
    const alreadySent = roomKeys.some((key) => sentRoomKeys.has(key));
    for (const key of roomKeys) {
      sentRoomKeys.add(key);
    }
    if (alreadySent) return;
    wsClient.send({ type: 'admin_set_minigame_override', minigameId });
    log?.({ minigameId, sourceType: msg.type, roomKeys });
  };

  const unsubscribe = wsClient.subscribe((raw) => {
    const msg = raw as ServerMessage;
    if (isBootForceGodModeEnabledMessage(msg)) {
      godModeReady = true;
      if (pendingRoomMessage != null) {
        maybeSendForRoom(pendingRoomMessage);
        pendingRoomMessage = null;
      }
    } else if (isBootForceGodModeInvalidMessage(msg)) {
      cleanup();
      return;
    }
    maybeSendForRoom(msg);
  });

  function cleanup(): void {
    unsubscribe();
    if (timeoutId !== undefined) {
      clearTimeoutFn(timeoutId);
      timeoutId = undefined;
    }
  }

  timeoutId = setTimeoutFn(cleanup, timeoutMs);
  return cleanup;
}
